import React, { ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import '../../assets/css/editor.scss';

import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetQuestionsQuery,
  useGetQuizQuery,
} from '@/modules/services';
import { useErrorHandler } from 'react-error-boundary';
import PlayerUi from '@/components/Player';
import { PreviewQuizParamType } from './routes';
import { processQuestion, useIsMobileScreenSize } from '@/lib/util';
import { AnswerDataType, QuestionDataType } from '@/modules/types/question';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';

const PreviewQuiz = (): ReactElement => {
  const handleError = useErrorHandler();
  const { elearningId, questionId } = useParams<PreviewQuizParamType>();

  const elearning = useGetElearningQuery(elearningId);
  const quiz = useGetQuizQuery(elearningId);
  const { data: elearningBehaviourSettingsData } =
    useGetElearningBehaviourSettingsQuery();

  const questions = useGetQuestionsQuery(
    {
      quizId: `${quiz.data?.id}`,
    },
    {
      skip: !quiz.data?.id || !quiz.data,
    },
  );

  useThemeStyle(elearning.data?.theme);
  useModuleStyle(elearning.data);

  const { isMobile } = useIsMobileScreenSize();
  const getQuestion: QuestionDataType[] = useMemo(() => {
    if (!questions.data) return [];

    return questions.data
      .filter((question) => question.id === questionId)
      .map((question) => ({
        ...question,
        questionMetadata: {
          ...question.questionMetadata,
          disabled: 0,
        },
      }));
  }, [questionId, questions]);

  if (elearning.isError || quiz.isError || questions.isError) {
    handleError(elearning.isError || quiz.error || questions.error);
  }

  if (
    elearning.isLoading ||
    quiz.isLoading ||
    questions.isLoading ||
    !questions.data
  ) {
    return <div className="loading" />;
  }

  const playerHandlerState = {
    questionsToAsk: questionId ? 1 : quiz.data?.quizMetadata.questionNo,
    questionsAsked: 0,
    correctAnswers: 0,
    modifiedQuestions: !questionId
      ? questions.data
      : questions.data.filter((question) => question.id === questionId),
    questionsCompleted: [],
  };

  const playerHandlers = {
    submitResult: (result: any, callback: () => void) => {
      // @todo : submit result to TMS
      callback();
    },
    submitQuestion: (
      answer: AnswerDataType,
      callback: (payload: any) => void,
    ) => {
      const nextPayload = processQuestion(answer, playerHandlerState);
      callback(nextPayload);
    },
    getQuestions: () => {
      playerHandlerState.questionsAsked = 0;
      playerHandlerState.correctAnswers = 0;
      playerHandlerState.modifiedQuestions = (
        !questionId
          ? questions.data
          : questions.data?.filter((question) => question.id === questionId)
      ) as QuestionDataType[];
      playerHandlerState.questionsCompleted = [];
    },
    submitAssessment: (quizData: any, callback: (payload: any) => void) => {
      const divisor = quiz.data?.quizMetadata.questionNo || 1;
      const score = Math.round(
        (playerHandlerState.correctAnswers / divisor) * 100,
      );

      callback({ score });
    },
    startAssessment: () => {},
    moveToFrame: (nextFrameId: string) => {},
  };

  const quizData = quiz.data
    ? {
        ...quiz.data,
        quizMetadata: {
          ...quiz.data.quizMetadata,
          questionNo: questionId ? 1 : quiz.data.quizMetadata.questionNo,
        },
        questions: !questionId ? questions.data : getQuestion,
      }
    : undefined;

  return (
    <PlayerUi
      isPreview={true}
      isPlayable={true}
      currentFrameId=""
      isQuizEnabled={true}
      elearning={elearning.data}
      frames={[]}
      quiz={quizData}
      resources={[]}
      helperContent={[]}
      helperFaq={[]}
      handlers={playerHandlers}
      isMobileView={isMobile}
      elearningBehaviourSettings={elearningBehaviourSettingsData}
    />
  );
};

export default PreviewQuiz;
